import { Card, CardContent, Input } from '../../../components/ui';

interface ModalSectionProps {
    modalEnabled: boolean;
    onModalEnabledChange: (enabled: boolean) => void;
    headerBadge?: string;
    onHeaderBadgeChange: (value: string) => void;
    headerTitle?: string;
    onHeaderTitleChange: (value: string) => void;
    headerSubtitle?: string;
    onHeaderSubtitleChange: (value: string) => void;
    primaryButtonText?: string;
    onPrimaryButtonTextChange: (value: string) => void;
    primaryButtonInfo?: string;
    onPrimaryButtonInfoChange: (value: string) => void;
    footerNote?: string;
    onFooterNoteChange: (value: string) => void;
}

export default function ModalSection({
    modalEnabled,
    onModalEnabledChange,
    headerBadge,
    onHeaderBadgeChange,
    headerTitle,
    onHeaderTitleChange,
    headerSubtitle,
    onHeaderSubtitleChange,
    primaryButtonText,
    onPrimaryButtonTextChange,
    primaryButtonInfo,
    onPrimaryButtonInfoChange,
    footerNote,
    onFooterNoteChange,
}: ModalSectionProps) {
    return (
        <Card>
            <CardContent className="p-6 space-y-6">
                <div className="flex items-center justify-between pb-4 border-b border-[#e2e8f0]">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#64748b] to-[#94a3b8] flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold text-lg">8</span>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-[#1e293b]">Modal/Popup</h3>
                            <p className="text-sm text-[#64748b]">
                                Configure a promotional popup modal for your microsite
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => onModalEnabledChange(!modalEnabled)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            modalEnabled ? 'bg-[#6e64f0]' : 'bg-[#cbd5e1]'
                        }`}
                    >
                        <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                modalEnabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                        />
                    </button>
                </div>

                {modalEnabled && (
                    <div className="space-y-4">
                        {/* Header Section */}
                        <div className="space-y-4 p-4 bg-[#f8fafc] border border-[#e2e8f0] rounded-lg">
                            <h4 className="text-sm font-semibold text-[#1e293b]">Modal Header</h4>

                            <Input
                                label="Header Badge (Optional)"
                                type="text"
                                value={headerBadge || ''}
                                onChange={(e) => onHeaderBadgeChange(e.target.value)}
                                placeholder="SPECIAL OFFER"
                                maxLength={20}
                            />

                            <Input
                                label="Header Title"
                                type="text"
                                value={headerTitle || ''}
                                onChange={(e) => onHeaderTitleChange(e.target.value)}
                                placeholder="Get 20% Off Your First Order"
                                maxLength={60}
                                showCharCount
                            />

                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <label className="block text-sm font-medium text-[#1e293b]">
                                        Header Subtitle
                                    </label>
                                    <span
                                        className={`text-xs ${(headerSubtitle?.length || 0) > 100 ? 'text-[#ef4444]' : 'text-[#64748b]'}`}
                                    >
                                        {headerSubtitle?.length || 0}/100
                                    </span>
                                </div>
                                <textarea
                                    value={headerSubtitle || ''}
                                    onChange={(e) => onHeaderSubtitleChange(e.target.value)}
                                    placeholder="Join our premium membership today"
                                    maxLength={100}
                                    rows={2}
                                    className="w-full px-4 py-2.5 bg-white border border-[#e2e8f0] rounded-lg text-[#1e293b] focus:outline-none focus:ring-2 focus:ring-[#6e64f0] focus:border-transparent resize-none"
                                />
                            </div>
                        </div>

                        {/* Primary Button Section */}
                        <div className="space-y-4 p-4 bg-[#f0fdf4] border border-[#bbf7d0] rounded-lg">
                            <h4 className="text-sm font-semibold text-[#1e293b]">
                                Call-to-Action Button
                            </h4>

                            <Input
                                label="Button Text"
                                type="text"
                                value={primaryButtonText || ''}
                                onChange={(e) => onPrimaryButtonTextChange(e.target.value)}
                                placeholder="Claim Offer"
                                maxLength={30}
                            />

                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <label className="block text-sm font-medium text-[#1e293b]">
                                        Button Info Text (Optional)
                                    </label>
                                    <span
                                        className={`text-xs ${(primaryButtonInfo?.length || 0) > 80 ? 'text-[#ef4444]' : 'text-[#64748b]'}`}
                                    >
                                        {primaryButtonInfo?.length || 0}/80
                                    </span>
                                </div>
                                <Input
                                    type="text"
                                    value={primaryButtonInfo || ''}
                                    onChange={(e) => onPrimaryButtonInfoChange(e.target.value)}
                                    placeholder="Terms and conditions apply. Offer valid till Dec 31"
                                    maxLength={80}
                                />
                                <p className="text-xs text-[#64748b]">
                                    Small text shown below the button
                                </p>
                            </div>
                        </div>

                        {/* Footer Note */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-[#1e293b]">
                                Footer Note (Optional)
                            </label>
                            <Input
                                type="text"
                                value={footerNote || ''}
                                onChange={(e) => onFooterNoteChange(e.target.value)}
                                placeholder="No credit card required"
                                maxLength={50}
                            />
                            <p className="text-xs text-[#64748b]">
                                Additional information displayed at the bottom of the modal
                            </p>
                        </div>

                        {/* Preview Hint */}
                        <div className="p-3 bg-[#fef3c7] border border-[#fde68a] rounded-lg">
                            <p className="text-xs text-[#92400e]">
                                <strong>💡 Preview:</strong> This modal will appear as a popup on
                                your microsite with the configured header, button, and footer
                                content.
                            </p>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
